import { useEffect, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";

export type TVodTrayMeta = {
  title?: string | null;
  matchId?: string | null;
  playerGameIdentifier?: string | null;
};

const VOD_TRAY_META = gql`
  query VodTrayMeta($id: String!) {
    clip(id: $id) {
      id
      matchId
      playerGameIdentifier
      title
    }
  }
`;

/** Vod cards already say "Match VOD" — drop the redundant title suffix. */
export const stripVodSuffix = (title?: string | null): string | undefined =>
  title?.replace(/\s*-\s*Match VOD\s*$/i, "") || undefined;

/** Tray link to the match-history VOD viewer; null when the montage has no matchId. */
export const matchVodTrayUrl = (meta?: TVodTrayMeta): string | null => {
  if (!meta?.matchId) return null;
  const params = new URLSearchParams({ vod: meta.matchId });
  if (meta.playerGameIdentifier)
    params.set("playerGameId", meta.playerGameIdentifier);
  return `/match-history?${params.toString()}`;
};

/** Fetch montage title + viewer link data for each vod group in the tray. */
export const useVodTrayMeta = (
  montageIds: string[],
): Record<string, TVodTrayMeta> => {
  const client = useApolloClient();
  const [metaById, setMetaById] = useState<Record<string, TVodTrayMeta>>({});

  useEffect(() => {
    const missing = montageIds.filter((id) => !(id in metaById));
    if (!missing.length) return;

    let cancelled = false;
    Promise.all(
      missing.map((id) =>
        client
          .query({ query: VOD_TRAY_META, variables: { id } })
          .then(
            ({ data }) =>
              [
                id,
                {
                  title: data?.clip?.title,
                  matchId: data?.clip?.matchId,
                  playerGameIdentifier: data?.clip?.playerGameIdentifier,
                },
              ] as const,
          )
          .catch(() => [id, {}] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setMetaById((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
  }, [montageIds, metaById, client]);

  return metaById;
};
